'use client';

// cSpell:ignore subtitl

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useValidExposure } from '@/hooks/useValidExposure';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useContext, useEffect, useMemo, type CSSProperties } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { useHomeSpmModuleCode } from '@/app/(home-page)/utils/spm-module';
import { buildHomeButtonTrackingParams } from '../../tracking';
import type {
  ImageTextGridNavDisplayStyle,
  ImageTextGridNavItem,
  ImageTextGridNavJumpConfig,
  ImageTextGridNavProps,
  ImageTextGridNavTrackingParams,
} from './types';

const COMPONENT_ID = 'imageTextGridNav';
const MIN_NAV_ITEMS = 3;
const MAX_NAV_ITEMS = 9;

interface StyleOneImagePresentation {
  imageSize: number;
  left: number;
  objectFit: CSSProperties['objectFit'];
  rotate: number;
  size: number;
  top: number;
}

// 装修数据不提供素材偏移参数，所有导航项统一使用 Figma 节点 6445:16508 的展示规则。
const STYLE_ONE_IMAGE_PRESENTATION: StyleOneImagePresentation = {
  imageSize: 130,
  left: -16.15,
  objectFit: 'contain',
  rotate: 15,
  size: 159.217,
  top: -28,
};

const formatText = (text?: string) => text?.replace(/<br\s*\/?>/gi, '\n') || '';

const getImageSize = (value: string | number | undefined, fallback: number) => {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : fallback;
};

const getItemLabel = (item: ImageTextGridNavItem, index: number) =>
  item.title || item.sku?.short_name || item.product_group?.name || `Navigation item ${index + 1}`;

const buildTrackingParams = ({
  item,
  itemIndex,
  moduleIndex,
  componentId,
  moduleId,
  moduleTitle,
}: {
  item: ImageTextGridNavJumpConfig;
  itemIndex: number;
  moduleIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
}): ImageTextGridNavTrackingParams => ({
  module_index: moduleIndex,
  module_name: componentId,
  module_id: moduleId,
  mkt_index: itemIndex,
  mkt_index_sub: 0,
  mkt_jump_type: String(item.jump_type || ''),
  mkt_jump_content: String(item.Jump_target || ''),
  module_tab_name: '',
  coupon_id_list: '',
  coupon_name_list: '',
  shop_sku: String(item.sku?.shop_sku || ''),
  business_sku: String(item.sku?.business_sku || ''),
  product_sku: String(item.sku?.sku || ''),
  product_id: String(item.sku?.id || ''),
  product_name: String(item.sku?.short_name || ''),
  group_id: String(item.product_group?.id || ''),
  group_name: String(item.product_group?.name || ''),
  module_title: moduleTitle,
});

function ImageTextGridNavItemView({
  item,
  itemIndex,
  displayStyle,
  moduleIndex,
  componentId,
  moduleId,
  moduleTitle,
  spmModule,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: {
  item: ImageTextGridNavItem;
  itemIndex: number;
  displayStyle: ImageTextGridNavDisplayStyle;
  moduleIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  /** 楼层完整动态 SPM 模块编码（C 位，`${component_id}_${id}`）。 */
  spmModule: string;
} & Pick<ImageTextGridNavProps, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'>) {
  const trackingParams = useMemo(
    () =>
      buildTrackingParams({
        item,
        itemIndex,
        moduleIndex,
        componentId,
        moduleId,
        moduleTitle,
      }),
    [componentId, item, itemIndex, moduleId, moduleIndex, moduleTitle],
  );
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });
  // 坑位完整编码（D 位）：框型为 shape_entry、镜片为 lens_entry，序号 2 位；点击与 item 级有效曝光共用。
  const entrySpmCnt = `${spmModule}.${displayStyle === 1 ? 'shape_entry' : 'lens_entry'}_${String(itemIndex + 1).padStart(2, '0')}`;
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与上报 spm_cnt 同值，表外静默不打标。
  const entryDomSpm = useSpmDomAttr(entrySpmCnt);
  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(withSpmCnt(trackingParams, entrySpmCnt)),
  });
  const itemRef = useMergedRefs(exposureRef, validExposureRef);

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('ImageTextGridNav exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      // spm_cnt 为坑位完整编码（D 位）；浅拷贝避免污染曝光/点击共享的 trackingParams。
      onTrack(withSpmCnt(trackingParams, entrySpmCnt));
    } catch (error) {
      logger.error('ImageTextGridNav click tracking error:', error);
    }
  }, [entrySpmCnt, onTrack, trackingParams]);

  const label = getItemLabel(item, itemIndex);
  const imageAlt = /\blens(?:es)?\b/i.test(moduleTitle)
    ? `${label} Lenses`
    : /\bshapes?\b/i.test(moduleTitle)
      ? `${label} Glasses`
      : label;
  const imageWidth = getImageSize(item.jump_img_width, displayStyle === 1 ? 92 : 32);
  const imageHeight = getImageSize(item.jump_img_height, displayStyle === 1 ? 92 : 32);
  const styleOneImagePresentation = STYLE_ONE_IMAGE_PRESENTATION;

  if (displayStyle === 2) {
    return (
      <div ref={itemRef} className="min-w-0">
        <MagicJump
          prefetch
          item={item}
          onBeforeJump={handleClick}
          aria-label={label}
          className="flex h-[44px] w-full min-w-0 items-center gap-[4px] rounded-[28px] px-[12px] py-[4px]"
          style={{ backgroundColor: item.text_bg_color || '#ffffff' }}
          {...entryDomSpm}
        >
          <span className="relative block size-[32px] shrink-0 overflow-hidden">
            <Image
              src={item.jump_image}
              alt={imageAlt}
              width={imageWidth}
              height={imageHeight}
              loading={moduleIndex < 3 ? 'eager' : 'lazy'}
              className="block size-full object-contain"
            />
          </span>
          <span
            className="min-w-0 flex-1 truncate font-br text-[12px] font-normal leading-[14px]"
            style={{ color: item.text_color || '#282828' }}
          >
            {formatText(label)}
          </span>
        </MagicJump>
      </div>
    );
  }

  return (
    <div ref={itemRef} className="min-w-0 self-start">
      <MagicJump
        prefetch
        item={item}
        onBeforeJump={handleClick}
        aria-label={label}
        className="mx-auto flex w-full max-w-[109px] min-w-0 flex-col items-center gap-[6px]"
        {...entryDomSpm}
      >
        <span className="relative block size-[92px] shrink-0 overflow-hidden rounded-full bg-[#f7f7f7]">
          <span
            className="pointer-events-none absolute flex items-center justify-center"
            style={{
              left: styleOneImagePresentation.left,
              top: styleOneImagePresentation.top,
              width: styleOneImagePresentation.size,
              height: styleOneImagePresentation.size,
            }}
          >
            <span
              className="relative block shrink-0"
              style={{
                width: styleOneImagePresentation.imageSize,
                height: styleOneImagePresentation.imageSize,
                aspectRatio: '1 / 1',
                transform: `rotate(${styleOneImagePresentation.rotate}deg)`,
              }}
            >
              <Image
                fill
                src={item.jump_image}
                alt={imageAlt}
                sizes={`${Math.ceil(styleOneImagePresentation.imageSize)}px`}
                loading={moduleIndex < 3 ? 'eager' : 'lazy'}
                className="pointer-events-none absolute inset-0 max-w-none"
                style={{ objectFit: styleOneImagePresentation.objectFit }}
              />
            </span>
          </span>
        </span>
        <span
          className="w-full whitespace-pre-line break-words text-center font-br text-[12px] font-normal leading-[14px]"
          style={{
            color: item.text_color || '#1a1c1e',
            backgroundColor: item.text_bg_color || '#ffffff',
          }}
        >
          {formatText(label)}
        </span>
      </MagicJump>
    </div>
  );
}

export default function ImageTextGridNav({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  onBtnTrack,
}: ImageTextGridNavProps) {
  const { deviceType } = useContext(ResponsiveContext);
  // 2026 新组件只读取新版 content 契约，不兼容旧装修组件的扁平数据。
  const content = indexData.content;
  const navItems = useMemo(
    () =>
      (content?.jump_target || [])
        .filter((item) => Boolean(item.jump_image && item.title))
        .slice(0, MAX_NAV_ITEMS),
    [content?.jump_target],
  );
  const displayStyle = Number(content?.display_style);

  // 楼层 SPM 模块编码（C 位）：按楼块数据动态拼接，坑位码在此基础上追加。
  const spmModule = useHomeSpmModuleCode(indexData.component_id, indexData.id, 'home_wap');
  // Data SPM DOM 打标（C 位模块码，请求头 data-spm=1 开关）：与楼层曝光上报的模块编码同值。
  const moduleDomSpm = useSpmDomAttr(spmModule);
  // View More 使用独立的 D 位编码 view_more，避免与第 7 个框型分类碰撞；
  // Lens 楼层与模块码缺失时保持空串不上报 spm_cnt。
  const viewMoreSpmCnt = displayStyle === 1 && spmModule ? `${spmModule}.view_more` : '';
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与上报 spm_cnt 同值，表外静默不打标。
  const viewMoreDomSpm = useSpmDomAttr(viewMoreSpmCnt);

  if (
    deviceType === EPlatform.WEB ||
    !content ||
    (displayStyle !== 1 && displayStyle !== 2) ||
    navItems.length < MIN_NAV_ITEMS
  ) {
    return null;
  }

  const normalizedDisplayStyle = displayStyle as ImageTextGridNavDisplayStyle;
  const componentId = indexData.component_id || COMPONENT_ID;
  const moduleId = String(indexData.blocks_advances_id || '');
  const moduleTitle = content.layout_title || '';
  const shouldShowHeader =
    Number(content.layout_title_status) === 1 &&
    Boolean(content.layout_title || content.layout_subtitle);
  const shouldShowViewMore =
    Number(content.view_more_status) === 1 &&
    Boolean(content.btn_text && content.jump_type && content.Jump_target);
  const viewMoreButtonName = content.btn_text || '';
  const handleViewMoreClick = () => {
    if (!onBtnTrack) return;

    try {
      const params = buildHomeButtonTrackingParams({
        indexData,
        moduleIndex,
        moduleTitle,
        buttonName: viewMoreButtonName,
      });
      onBtnTrack(viewMoreSpmCnt ? withSpmCnt(params, viewMoreSpmCnt) : params);
    } catch (error) {
      logger.error('ImageTextGridNav view more tracking error:', error);
    }
  };

  return (
    <section
      data-component-id={componentId}
      className={normalizedDisplayStyle === 2 ? 'w-full pb-[20px]' : 'w-full pb-[12px]'}
      style={
        Number(content.bg_color_status) === 1 && content.bg_color
          ? { backgroundColor: content.bg_color }
          : undefined
      }
      {...moduleDomSpm}
    >
      {shouldShowHeader && (
        <header className="px-[12px] pt-[20px] pb-[12px] text-left">
          {content.layout_title && (
            <h3
              className="line-clamp-2 whitespace-pre-line font-bb text-[20px] leading-[24px]"
              style={{ color: content.layout_title_color || '#282828' }}
            >
              {formatText(content.layout_title)}
            </h3>
          )}
          {content.layout_subtitle && (
            <h4
              className="mt-[4px] line-clamp-3 whitespace-pre-line font-br text-[12px] font-normal leading-[14px]"
              style={{
                color: content.layout_subtitle_color || content.layout_subtitl_color || '#666666',
              }}
            >
              {formatText(content.layout_subtitle)}
            </h4>
          )}
        </header>
      )}

      <div
        className={
          normalizedDisplayStyle === 1
            ? 'grid grid-cols-3 gap-x-[12px] gap-y-[12px] px-[6px]'
            : 'grid grid-cols-2 gap-[8px] px-[12px]'
        }
      >
        {navItems.map((item, itemIndex) => (
          <ImageTextGridNavItemView
            key={item.id || `image-text-grid-nav-${itemIndex}`}
            item={item}
            itemIndex={itemIndex}
            displayStyle={normalizedDisplayStyle}
            moduleIndex={moduleIndex}
            componentId={componentId}
            moduleId={moduleId}
            moduleTitle={moduleTitle}
            spmModule={spmModule}
            onTrack={onTrack}
            onExpTrack={onExpTrack}
            onValidExpTrack={onValidExpTrack}
          />
        ))}
      </div>

      {shouldShowViewMore && (
        <div className="flex justify-center px-[12px] pt-[12px] pb-[12px]">
          <MagicJump
            prefetch
            item={content}
            onBeforeJump={handleViewMoreClick}
            className="flex h-[40px] w-full items-center justify-center rounded-[20px] border border-[#808080] px-[20px] font-bb text-[14px] leading-[17px]"
            style={{
              color: content.btn_word_color || '#282828',
              backgroundColor: content.btn_bg_color || '#ffffff',
            }}
            {...viewMoreDomSpm}
          >
            <span style={{ color: content.btn_word_color || '#282828' }}>{content.btn_text}</span>
          </MagicJump>
        </div>
      )}
    </section>
  );
}
